import { PDFPageRef } from '../types';
import { UnifiedFormField } from './pdfFieldUtils';
import { Comment } from '../CommentsSidebar';
import { Strikeout } from '../StrikeoutOverlay';
import { FreeText } from '../FreeTextOverlay';

interface ApplyPageOperationResultParams {
  newPdfBytes: Uint8Array;
  updatedPages: PDFPageRef[];
  reconciledFields: UnifiedFormField[];
  reconciledComments: Comment[];
  reconciledStrikeouts: Strikeout[];
  reconciledFreeTexts: FreeText[];
  documentId: string | undefined;
  setPages: (pages: PDFPageRef[]) => void;
  setComments: (comments: Comment[]) => void;
  setStrikeouts: (strikeouts: Strikeout[]) => void;
  setFreeTexts: (freeTexts: FreeText[]) => void;
  setAllFields: (fields: UnifiedFormField[]) => void;
  setPdfBytes: (bytes: Uint8Array) => void;
  setModifiedPdfUrl: (updater: (prev: string | null) => string | null) => void;
  setPageRevision?: (updater: (prev: number) => number) => void;
  saveAll: (
    formFields: any[],
    comments: any[],
    strikeouts: any[],
    freeTexts: any[]
  ) => void;
  markDirty: (type: 'fields') => void;
  uploadModifiedPdf: (bytes: Uint8Array) => Promise<void>;
  prepareDocumentForSave: (
    fields: UnifiedFormField[],
    comments: Comment[],
    strikeouts: Strikeout[],
    freeTexts: FreeText[]
  ) => {
    formFields: any[];
    comments: any[];
    strikeouts: any[];
    freeTexts: any[];
  };
}

export const applyPageOperationResult = async ({
  newPdfBytes,
  updatedPages,
  reconciledFields,
  reconciledComments,
  reconciledStrikeouts,
  reconciledFreeTexts,
  documentId,
  setPages,
  setComments,
  setStrikeouts,
  setFreeTexts,
  setAllFields,
  setPdfBytes,
  setModifiedPdfUrl,
  setPageRevision,
  saveAll,
  markDirty,
  uploadModifiedPdf,
  prepareDocumentForSave,
}: ApplyPageOperationResultParams): Promise<void> => {
  if (documentId) {
    const { formFields, comments: pdfComments, strikeouts: pdfStrikeouts, freeTexts: pdfFreeTexts } =
      prepareDocumentForSave(reconciledFields, reconciledComments, reconciledStrikeouts, reconciledFreeTexts);

    await uploadModifiedPdf(newPdfBytes);

    setPages(updatedPages);
    if (setPageRevision) {
      setPageRevision(prev => prev + 1);
    }
    setComments(reconciledComments);
    setStrikeouts(reconciledStrikeouts);
    setFreeTexts(reconciledFreeTexts);
    setAllFields(reconciledFields);
    setPdfBytes(newPdfBytes);

    const blob = new Blob([newPdfBytes.slice()], { type: 'application/pdf' });
    const newUrl = URL.createObjectURL(blob);
    setModifiedPdfUrl(prev => {
      if (prev) URL.revokeObjectURL(prev);
      return newUrl;
    });

    saveAll(formFields, pdfComments, pdfStrikeouts, pdfFreeTexts);
    markDirty('fields');
  } else {
    setPages(updatedPages);
    if (setPageRevision) {
      setPageRevision(prev => prev + 1);
    }
    setComments(reconciledComments);
    setStrikeouts(reconciledStrikeouts);
    setFreeTexts(reconciledFreeTexts);
    setAllFields(reconciledFields);
    setPdfBytes(newPdfBytes);

    const blob = new Blob([newPdfBytes.slice()], { type: 'application/pdf' });
    const newUrl = URL.createObjectURL(blob);
    setModifiedPdfUrl(prev => {
      if (prev) URL.revokeObjectURL(prev);
      return newUrl;
    });
  }
};
